// Admin status helpers — token-based.
//
// `auth.tsx`'s `useAuth().isAdmin` and this file's helpers check the actual
// ID token custom claim, which is what the backend uses to authorize admin
// callables. The Firestore-mirrored `role` field is diagnostic context only.
//
// `isAdminFromToken` is the authoritative check for "will this user's API
// calls succeed against the admin callables?".

import type { User } from 'firebase/auth';
import { auth } from './firebase';

export async function isAdminFromToken(user: User | null = null): Promise<boolean> {
  const u = user ?? auth.currentUser;
  if (!u) return false;
  try {
    const t = await u.getIdTokenResult();
    return t.claims.admin === true;
  } catch (e) {
    console.error('[admin] failed to read ID token claims:', e);
    return false;
  }
}

// Force-refreshes the ID token. Call this after the seed:admin script runs
// so the new `admin: true` claim shows up without requiring a sign-out.
export async function refreshAuthToken(): Promise<void> {
  const u = auth.currentUser;
  if (!u) return;
  await u.getIdToken(true);
}

// Returns the full claims object — useful for the diagnostic page so the
// admin can see exactly what the backend sees.
export async function getTokenClaims(): Promise<Record<string, unknown>> {
  const u = auth.currentUser;
  if (!u) return {};
  const t = await u.getIdTokenResult();
  return { ...t.claims };
}
